import {
  FC,
  createContext,
  useContext,
  useState,
  ReactNode,
  useEffect,
} from 'react'

import { Category, Program, RawCategory } from 'types'
import { language } from 'config'
import en from 'languages/en'
import ru from 'languages/ru'
import { localizeCategories, localizeColumns } from './helpers'
import { Loader } from 'components/loader'
import { AnimatePresence } from 'framer-motion'

type ContextType = {
  texts: typeof en
  programs: Program[]
  categories: Category[]
}

const locales = {
  en: {
    texts: en,
  },
  ru: {
    texts: ru,
  },
}

const texts = locales[language].texts as typeof en
const LocaleContext = createContext<ContextType>(null as unknown as ContextType)

export const LocaleContextProvider: FC<{ children?: ReactNode }> = ({
  children,
}) => {
  const [isLoading, setLoading] = useState(true)
  const [programs, setPrograms] = useState<Program[]>([])
  const [categories, setCategories] = useState<Category[]>([])

  const fetchData = async () => {
    const data = await fetch(`${process.env.PUBLIC_URL}/data/data.json`)
      .then((res) => res.text())
      .then((res) => {
        const json = JSON.parse(res.replaceAll(/\n/g, ''))
        return {
          programs: localizeColumns(json.data),
          categories: localizeCategories(
            json.enums.filter((cat: RawCategory) =>
              Object.keys(cat).every((k) => !!cat[k as keyof RawCategory])
            ) as RawCategory[]
          ),
        }
      })

    setPrograms(
      data.programs.map((p) => ({
        ...p,
        category:
          data.categories.find(
            (c) => (p.category as unknown as string) === c.id
          ) ?? null,
      }))
    )
    setCategories(data.categories)
  }

  useEffect(() => {
    void Promise.all([
      fetchData(),
      new Promise((resolve) => {
        setTimeout(resolve, 500)
      }),
    ]).then(() => {
      setLoading(false)
    })
  }, [])

  return (
    <LocaleContext.Provider value={{ programs, categories, texts }}>
      <AnimatePresence mode="wait">{isLoading && <Loader />}</AnimatePresence>
      {!isLoading && children}
    </LocaleContext.Provider>
  )
}

export const useLocale = () => {
  const context = useContext(LocaleContext)
  return context
}
